import axios, {AxiosResponse} from 'axios'
import {ID, QueryState, Response} from '../../../../../../_metronic/helpers'
import {ResponseTask, Task, TasksQueryResponse} from './_models'
import {ResponseTaskAttachement, TaskAttachement} from '../TaskAttachment/core/_models'
import {UserModel} from '../../../../auth'

const API_URL = process.env.REACT_APP_THEME_API_URL
const GET_TASK_URL = `${API_URL}/api/tarefas`
const GET_TASKATTACHEMENT_URL = `${API_URL}/api/tarefas/anexo`

const getTaskById = (id: ID): Promise<ResponseTask> => {
  return axios
    .get(`${GET_TASK_URL}/${id}`)
    .then((response: AxiosResponse<ResponseTask>) => response.data)
}

const getTaskAdjustTimeById = (id: ID): Promise<ResponseTask> => {
  return axios
    .get(`${GET_TASK_URL}/${id}/view-adjust-time`)
    .then((response: AxiosResponse<ResponseTask>) => response.data)
}

const getTasksList = (query: string): Promise<TasksQueryResponse> => {
  return axios
    .get(`${GET_TASK_URL}?${query}`)
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const updateNameTask = (task_id: ID, query: string): Promise<TasksQueryResponse> => {
  return axios
    .get(`${GET_TASK_URL}/${task_id}/${query}/updatenamecell`)
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const updateStatusTask = (
  task_id: ID,
  query: string,
  userId?: number,
  order?: number
): Promise<TasksQueryResponse> => {
  return axios
    .get(
      `${GET_TASK_URL}/${task_id}/${query}/updatestatuscell${
        userId ? `?userId=${userId}&order=${order}` : ''
      }`
    )
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const updateStageProject = (
  task_id: ID,
  stageName: string,
  order: number,
  projectId: number
): Promise<TasksQueryResponse> => {
  return axios
    .put(`${GET_TASK_URL}/${task_id}/stage/${stageName}/order/${order}/project/${projectId}`)
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const createTask = (task: Task): Promise<ResponseTask | undefined> => {
  return axios
    .post(`${GET_TASK_URL}/novo`, task)
    .then((response: AxiosResponse<ResponseTask>) => response.data)
}

const createTaskRow = (value: string, project_id: ID, expected_date?: string | null): Promise<any> => {
  const params = new URLSearchParams()
  if (expected_date && expected_date.trim() !== '') {
    params.append('expected_date', expected_date)
  }
  const query = params.toString()
  const url = `${GET_TASK_URL}/${value}/${project_id}/new-row${query ? `?${query}` : ''}`
  return axios.get(url).then((response) => response.data)
}

const cloneTask = (task: Partial<Task>): Promise<ResponseTask> => {
  let formData = new FormData()

  // Tarefa de origem: o backend confere a instância dela e responde 404 sem esse campo.
  if (task.task_id) {
    formData.append('task_id', String(task.task_id))
  }
  if (task.creator_id) {
    formData.append('creator_id', task.creator_id)
  }
  if (task.name) {
    formData.append('name', task.name)
  }
  if (task.description) {
    formData.append('description', task.description)
  }
  if (task.begin_date) {
    formData.append('begin_date', task.begin_date)
  }
  if (task.expected_date) {
    formData.append('expected_date', task.expected_date)
  }
  if (task.file) {
    task.file.map((file) => {
      formData.append('documents[]', file)
    })
  }
  if (Array.isArray(task.checklist_concluded)) {
    formData.append('checklist_concluded', JSON.stringify(task.checklist_concluded))
  }
  if (Array.isArray(task.checklist_name)) {
    formData.append('checklist_name', JSON.stringify(task.checklist_name))
  }
  if (task.project_id) {
    formData.append('project_id', task.project_id)
  }
  if (task.users) {
    formData.append('users', JSON.stringify(task.users))
  }
  if (task.status) {
    formData.append('status', task.status)
  }
  if (task.sprintId) {
    formData.append('sprintId', task.sprintId.toString())
  }
  if (task.tags) {
    formData.append('tags', JSON.stringify(task.tags))
  }
  if (task.sequence_responsibles && Array.isArray(task.sequence_responsibles)) {
    const sequence_responsibles = task.sequence_responsibles.reduce(
      (acc: any, user: any, index: any) => {
        const {task_expected_date, ...rest} = user
        const expectedDate = user.expected_date ?? task_expected_date ?? null
        acc[Number(user.user_id ?? user.id)] = {
          order: index,
          expected_date: expectedDate && expectedDate !== '' && expectedDate !== 'null' ? expectedDate : null,
        }
        return acc
      },
      {} as {[key: number]: {order: number; expected_date?: string | null}}
    )
    formData.append('sequence_responsibles', JSON.stringify(sequence_responsibles))
  }
  if (!task.users) formData.append('users', JSON.stringify(task.sequence_responsibles))

  return axios
    .post(`${GET_TASK_URL}/clone-task`, formData)
    .then((response: AxiosResponse<ResponseTask>) => response.data)
}

const updateTask = (task: any): Promise<ResponseTask> => {
  if (task.sequence_responsibles && Array.isArray(task.sequence_responsibles)) {
    const sequence_responsibles = task.sequence_responsibles.reduce(
      (
        acc: {
          responsible_id: number
          order: number
          concluded: boolean
          expected_date?: string | null
        }[],
        user: any,
        index: number
      ) => {
        const expectedDate = user.expected_date ?? user.task_expected_date ?? null
        const {task_expected_date, ...rest} = user
        acc.push({
          responsible_id: Number(user.user_id ?? user.responsible_id ?? user.id),
          order: index,
          concluded: user.task_concluded ?? false,
          expected_date: expectedDate && expectedDate !== '' && expectedDate !== 'null' ? expectedDate : null,
        })

        return acc
      },
      []
    )

    task.sequence_responsibles = sequence_responsibles
  }

  const cleanedTask = Object.keys(task).reduce((acc: any, key: string) => {
    const value = task[key]

    if (key === 'begin_date' || key === 'expected_date') {
      acc[key] = value
    }

    else if (key === 'priority') {
      acc[key] = value
    }

    else if (key === 'recurrence') {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        if (!value.frequency || (Object.keys(value).length === 0)) {
          acc[key] = null
        } else {
          acc[key] = value
        }
      } else {
        acc[key] = value
      }
    }
    // Arrays de checklist devem ser enviados mesmo quando vazios para limpar no backend
    else if (key === 'checklist_concluded' || key === 'checklist_name') {
      acc[key] = value // Incluir mesmo se for array vazio
    }
    // Outros campos: incluir apenas se não são null, undefined ou string vazia
    else if (value !== null && value !== undefined && value !== '') {
      acc[key] = value
    }

    return acc
  }, {})

  return axios
    .put(`${GET_TASK_URL}/${task.id}/editar`, cleanedTask)
    .then((response: AxiosResponse<ResponseTask>) => response.data)
}

const getUsersByTaskId = (taskId: ID): Promise<ResponseTask> => {
  return axios
    .get(`${GET_TASK_URL}/${taskId}/getUsers`)
    .then((response: AxiosResponse<ResponseTask>) => response.data)
}

const getTasksByProjectId = (id: string, query: string): Promise<TasksQueryResponse> => {
  return axios
    .get(`${GET_TASK_URL}/${id}/by-project?${query}`)
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const deliveryTask = (taskId: ID): Promise<ResponseTask> => {
  return axios
    .get(`${GET_TASK_URL}/${taskId}/delivery-task`)
    .then((d: AxiosResponse<ResponseTask>) => d.data)
}

const deliveryTaskPartial = (taskId: ID): Promise<ResponseTask> => {
  return axios
    .get(`${GET_TASK_URL}/${taskId}/delivery-task?partial=1`)
    .then((d: AxiosResponse<ResponseTask>) => d.data)
}

const reopenTaskMyPart = (taskId: ID): Promise<ResponseTask> => {
  return axios
    .get(`${GET_TASK_URL}/${taskId}/reopen-my-part`)
    .then((d: AxiosResponse<ResponseTask>) => d.data)
}

const deleteTask = (taskId: ID): Promise<void> => {
  return axios.delete(`${GET_TASK_URL}/${taskId}/excluir`).then(() => {})
}

const deleteTaskInSprint = (taskId: ID, sprintId: ID): Promise<void> => {
  return axios.delete(`${GET_TASK_URL}/${taskId}/${sprintId}/excluir-sprint`).then(() => {})
}

const deleteSelectedTasks = (taskIds: Array<ID>): Promise<void> => {
  const requests = taskIds.map((id) => axios.delete(`${GET_TASK_URL}/${id}`))
  return axios.all(requests).then(() => {})
}
const searchTask = (search: string, project_id: ID): Promise<TasksQueryResponse> => {
  let formData = new FormData()

  formData.append('search', search)
  formData.append('project_id', project_id!.toString())

  return axios
    .post(`${GET_TASK_URL}/search`, formData)
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const getAttachements = (id: ID): Promise<TasksQueryResponse> => {
  return axios
    .get(`${GET_TASKATTACHEMENT_URL}/${id}`)
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const createTaskAttachement = (
  taskAttachement: TaskAttachement
): Promise<ResponseTaskAttachement> => {
  let formData = new FormData()
  if (taskAttachement.file) {
    formData.append('file', taskAttachement.file)
  }
  if (taskAttachement.task_id) {
    formData.append('task_id', taskAttachement.task_id)
  }
  return axios
    .post(`${GET_TASKATTACHEMENT_URL}/novo`, formData)
    .then((response: AxiosResponse<ResponseTaskAttachement>) => response.data)
    .catch((error) => {
      // Se a resposta contém dados de erro estruturados, retorna eles
      if (error.response && error.response.data) {
        return Promise.reject(error.response.data)
      }
      // Caso contrário, rejeita com o erro original
      return Promise.reject(error)
    })
}

const deleteAttachement = (attachementid: ID): Promise<void> => {
  return axios.delete(`${GET_TASKATTACHEMENT_URL}/${attachementid}/excluir`).then(() => {})
}

const playTimeTracking = (taskId: ID): Promise<TasksQueryResponse> => {
  return axios
    .get(`${GET_TASK_URL}/${taskId}/timetracking/start`)
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const stopTimeTracking = (taskId: ID): Promise<TasksQueryResponse> => {
  return axios
    .get(`${GET_TASK_URL}/${taskId}/timetracking/stop`)
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const getAssignableSubtasks = (search: string): Promise<TasksQueryResponse> => {
  return axios
    .get(`${GET_TASK_URL}/assignable-subtasks?search=${search}`)
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const createSubtask = (subtask: Task): Promise<any> => {
  return axios
    .post(`${GET_TASK_URL}/subtasks/`, subtask)
    .then((d: AxiosResponse<any>) => d.data.data)
}

const deleteSubtask = (id: number): Promise<TasksQueryResponse> => {
  return axios.delete(`${GET_TASK_URL}/subtasks/${id}`).then((d: AxiosResponse<any>) => d.data.data)
}

const getKanbanTasksByProjectId = (id: number, query: string): Promise<TasksQueryResponse> => {
  return axios
    .get(`${GET_TASK_URL}/${id}/by-project/kanban?${query}`)
    .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
}

const getSubtasksByParentId = (parentId: ID, projectId?: ID): Promise<TasksQueryResponse> => {
  const params = new URLSearchParams()
  if (parentId !== null && parentId !== undefined) {
    params.append('parent_id', parentId.toString())
  }
  if (projectId) {
    // Usar endpoint de tarefas por projeto
    return axios
      .get(`${GET_TASK_URL}/${projectId}/by-project?${params.toString()}`)
      .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
  } else {
    // Usar endpoint geral de tarefas
    return axios
      .get(`${GET_TASK_URL}?${params.toString()}`)
      .then((d: AxiosResponse<TasksQueryResponse>) => d.data)
  }
}

export {
  deleteTask,
  deleteTaskInSprint,
  deleteSelectedTasks,
  getTaskById,
  getUsersByTaskId,
  getTaskAdjustTimeById,
  createTask,
  updateTask,
  deliveryTask,
  deliveryTaskPartial,
  reopenTaskMyPart,
  cloneTask,
  getTasksByProjectId,
  searchTask,
  getAttachements,
  deleteAttachement,
  getTasksList,
  createTaskRow,
  updateNameTask,
  playTimeTracking,
  stopTimeTracking,
  createTaskAttachement,
  updateStatusTask,
  updateStageProject,
  getAssignableSubtasks,
  createSubtask,
  deleteSubtask,
  getKanbanTasksByProjectId,
  getSubtasksByParentId
}
